import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth";
import {
  acquireSubscription,
  type SubscriptionHandle,
} from "../api/realtime";
import { invalidateChatRoomLists } from "../api/chatApi";

/**
 * App-wide GG realtime listener. Mounted once in the layout (TopBar) so that
 * messages arriving in rooms the user does NOT have open still refresh the
 * sidebar badge + room list (unread count, preview, order).
 *
 * ONE subscription, on the personal `chat-user:{user_id}` channel. The backend
 * nudges it on every event that can change the room list: new/edited/deleted
 * message (fanned out to all members — see `_nudge_room_members` in
 * apps/chat/signals.py), room rename/member change, and membership changes
 * (room created, added/removed, read-state sync across tabs).
 *
 * This used to subscribe to `chat:{room_id}` for EVERY room in the list, only
 * to call the same invalidation on any payload. That cost one subscription and
 * one renewed token per room: a user with 42 rooms minted 42 tokens in a single
 * burst every time they expired, blew through the 300/min throttle, and each
 * 429 was retried into the same exhausted bucket — the chat froze until the
 * client gave up. The open room still gets its own `chat:{room_id}`
 * subscription in useChatRoomLive, which is where message payloads and typing
 * indicators are actually consumed.
 */

export function useChatGlobalLive(enabled: boolean) {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  useEffect(() => {
    if (!enabled || !userId) return;
    let cancelled = false;
    let cleanup: (() => void) | null = null;

    (async () => {
      try {
        const h: SubscriptionHandle = await acquireSubscription(
          `chat-user:${userId}`,
        );
        if (cancelled) {
          h.release();
          return;
        }
        // A busy room nudges this channel on EVERY message, and the room list is
        // the most expensive list in the app to serve — invalidateChatRoomLists
        // coalesces a burst into one refetch (shared with the open room's channel).
        const handler = () => invalidateChatRoomLists(qc);
        h.sub.on("publication", handler);
        cleanup = () => {
          h.sub.off("publication", handler);
          h.release();
        };
      } catch (err) {
        console.warn("[GG] chat-user subscription failed", err);
      }
    })();

    return () => {
      cancelled = true;
      try {
        cleanup?.();
      } catch {
        // ignore
      }
    };
  }, [enabled, userId, qc]);
}
